import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useItems } from '../hooks/useApi';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { Plus, Search, Package, Pencil, Trash2, Loader2 } from 'lucide-react';

const emptyItem = {
  name: '',
  description: '',
  default_price: 0,
  unit: 'pcs'
};

const ItemsPage = () => {
  const { getItems, createItem, updateItem, deleteItem, loading } = useItems();
  
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyItem);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    const { data } = await getItems();
    if (data) setItems(data);
  };

  const filteredItems = items.filter(i => 
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    i.description?.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingId(item.id);
      setForm({
        name: item.name,
        description: item.description || '',
        default_price: item.default_price || 0,
        unit: item.unit || 'pcs'
      });
    } else {
      setEditingId(null);
      setForm(emptyItem);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Item name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const { error } = await updateItem(editingId, form);
        if (!error) {
          toast.success('Item updated');
          loadItems();
          setDialogOpen(false);
        }
      } else {
        const { error } = await createItem(form);
        if (!error) {
          toast.success('Item created');
          loadItems();
          setDialogOpen(false);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error } = await deleteItem(deleteId);
    if (!error) {
      toast.success('Item deleted');
      loadItems();
    }
    setDeleteId(null);
  };

  return (
    <div className="space-y-6" data-testid="items-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Items</h1>
          <p className="text-muted-foreground mt-1">Manage your products and services</p>
        </div>
        <Button 
          data-testid="add-item-btn"
          onClick={() => handleOpenDialog()}
          className="gap-2 bg-accent hover:bg-accent/90"
        >
          <Plus className="w-4 h-4" />
          Add Item
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="item-search"
              placeholder="Search items..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Items List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {filteredItems.length} Item{filteredItems.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="empty-state">
              <Package className="w-12 h-12 mb-4 opacity-50" />
              <p className="font-medium">No items found</p>
              <p className="text-sm">
                {search ? 'Try adjusting your search' : 'Add your first item'}
              </p>
              {!search && (
                <Button 
                  className="mt-4 gap-2" 
                  onClick={() => handleOpenDialog()}
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Default Price</th>
                    <th>Unit</th>
                    <th className="w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => (
                    <tr key={item.id} data-testid={`item-row-${item.id}`}>
                      <td className="font-medium">{item.name}</td>
                      <td className="text-muted-foreground">
                        {item.description || '-'}
                      </td>
                      <td className="font-mono">
                        {formatCurrency(item.default_price)}
                      </td>
                      <td className="text-muted-foreground">{item.unit}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleOpenDialog(item)}
                            data-testid={`edit-item-${item.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => setDeleteId(item.id)}
                            data-testid={`delete-item-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Edit Item' : 'Add Item'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                data-testid="item-form-name"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Item name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                data-testid="item-form-description"
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Item description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Default Price</Label>
                <Input
                  data-testid="item-form-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.default_price}
                  onChange={(e) => setForm(f => ({ ...f, default_price: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Unit</Label>
                <Input
                  data-testid="item-form-unit"
                  value={form.unit}
                  onChange={(e) => setForm(f => ({ ...f, unit: e.target.value }))}
                  placeholder="pcs, hours, etc."
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              data-testid="item-form-save"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingId ? 'Update' : 'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Item?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The item will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ItemsPage;
